import { useCallback, useEffect, useRef, useState } from "react";
import { SIGNATURE_EXPORT_INK } from "@shared/agreement";

/**
 * Hand-drawn signature capture.
 *
 * Written rather than pulled from a library because the requirement is narrow
 * and the alternatives carry more surface area than the ~150 lines here.
 *
 * Details that matter and are easy to get wrong:
 *  - Pointer Events, so mouse, finger and stylus all work from one code path.
 *  - setPointerCapture, so a stroke that leaves the canvas still finishes
 *    cleanly instead of leaving a half-drawn line.
 *  - touchAction: "none", without which a phone scrolls the page instead of
 *    drawing, which is the single most common way a signature pad fails on
 *    mobile.
 *  - Backing store scaled to devicePixelRatio, so the exported PNG is sharp
 *    when it is later embedded in a PDF.
 *  - The exported PNG is TRIMMED to the ink's bounding box, so the signature
 *    fills its space in the PDF rather than floating in a mostly-empty canvas.
 *  - The ON-SCREEN ink color and the EXPORTED ink color are deliberately
 *    different colors, painted onto two separate canvases. On screen, ink is
 *    theme-colored (near-white on the dark pages this is used on) so it is
 *    visible against the page background while signing. But the agreement is
 *    a WHITE document, and a signature exported in that same near-white color
 *    would be stamped onto page 13 nearly invisibly - which is exactly what
 *    happened before this was split out: signatures rendered as a barely
 *    visible pale ghost. The exported PNG is always repainted in a fixed dark
 *    ink color (EXPORT_INK_COLOR) from the same vector stroke data, regardless
 *    of what color is shown on screen while drawing.
 */
const EXPORT_INK_COLOR = SIGNATURE_EXPORT_INK.hex;
export interface SignaturePadProps {
  /** Called with a trimmed PNG data URL, or null once cleared. */
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  theme?: "dark" | "light";
  height?: number;
}

type Point = { x: number; y: number };

/**
 * Paints stroke geometry onto a 2D context in a given color.
 *
 * Pure function shared by the on-screen repaint (theme ink color) and the
 * export render (always EXPORT_INK_COLOR), so the two can never drift apart in
 * anything but color - same points, same smoothing, same line width.
 */
function paintStrokes(ctx: CanvasRenderingContext2D, strokes: Point[][], color: string) {
  ctx.lineWidth = 2.2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  for (const stroke of strokes) {
    if (stroke.length === 0) continue;
    ctx.beginPath();
    if (stroke.length === 1) {
      // A deliberate dot still needs to render.
      ctx.arc(stroke[0].x, stroke[0].y, 1.1, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.moveTo(stroke[0].x, stroke[0].y);
    for (let i = 1; i < stroke.length; i++) {
      // Midpoint quadratic smoothing, so a fast stroke reads as a curve
      // rather than a run of visible straight segments.
      const prev = stroke[i - 1], pt = stroke[i];
      ctx.quadraticCurveTo(prev.x, prev.y, (prev.x + pt.x) / 2, (prev.y + pt.y) / 2);
    }
    ctx.stroke();
  }
}

export default function SignaturePad({
  onChange,
  disabled,
  theme = "dark",
  height = 170,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Point[][]>([]);
  const currentRef = useRef<Point[] | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const dark = theme === "dark";
  const inkColor = dark ? "#f8fafc" : "#0f172a";

  /** Repaints every stroke on screen, in the theme-appropriate ink color. */
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    paintStrokes(ctx, strokesRef.current, inkColor);
  }, [inkColor]);

  // Size the backing store to the element's real pixel size.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      repaint();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [repaint]);

  /**
   * Exports the ink, cropped to its bounding box with a small margin.
   * Returns null when there is no ink to export.
   *
   * Renders onto a FRESH offscreen canvas using EXPORT_INK_COLOR, from the
   * same vector stroke data as the on-screen canvas - it does NOT read pixels
   * from the visible canvas. That is what guarantees a dark, visible signature
   * on the white agreement page regardless of what color happens to be shown
   * on screen while the person is drawing.
   */
  const exportPng = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    if (!strokesRef.current.some(s => s.length > 0)) return null;

    const { width, height: h } = canvas;
    const render = document.createElement("canvas");
    render.width = width;
    render.height = h;
    const rctx = render.getContext("2d");
    if (!rctx) return null;
    paintStrokes(rctx, strokesRef.current, EXPORT_INK_COLOR);

    let data: Uint8ClampedArray;
    try {
      data = rctx.getImageData(0, 0, width, h).data;
    } catch {
      // Should not happen (canvas we just created and drew to ourselves),
      // but never lose a signature over a readback failure.
      return render.toDataURL("image/png");
    }

    let minX = width, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < width; x++) {
        if (data[(y * width + x) * 4 + 3] > 8) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;

    const pad = Math.round(6 * (window.devicePixelRatio || 1));
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);

    const out = document.createElement("canvas");
    out.width = maxX - minX + 1;
    out.height = maxY - minY + 1;
    const octx = out.getContext("2d");
    if (!octx) return render.toDataURL("image/png");
    octx.drawImage(render, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
    return out.toDataURL("image/png");
  }, []);

  const commit = useCallback(() => {
    const ink = strokesRef.current.some(s => s.length > 0);
    setHasInk(ink);
    onChange(ink ? exportPng() : null);
  }, [exportPng, onChange]);

  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    currentRef.current = [pointFrom(e)];
    strokesRef.current.push(currentRef.current);
    repaint();
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !currentRef.current) return;
    e.preventDefault();
    currentRef.current.push(pointFrom(e));
    repaint();
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!currentRef.current) return;
    e.preventDefault();
    currentRef.current = null;
    commit();
  };

  const clear = () => {
    strokesRef.current = [];
    currentRef.current = null;
    repaint();
    commit();
  };

  const undo = () => {
    strokesRef.current.pop();
    repaint();
    commit();
  };

  const frame = dark
    ? "bg-white/5 border-white/15"
    : "bg-white border-gray-200";
  const guide = dark ? "bg-white/20" : "bg-gray-300";
  const label = dark ? "text-white/40" : "text-gray-400";
  const btn = dark
    ? "text-white/50 hover:text-white/80 border-white/15 hover:border-white/30"
    : "text-gray-500 hover:text-gray-800 border-gray-200 hover:border-gray-300";

  return (
    <div>
      <div className={`relative rounded-lg border overflow-hidden ${frame} ${disabled ? "opacity-50" : ""}`}>
        <canvas
          ref={canvasRef}
          style={{ height, touchAction: "none" }}
          className={`w-full block ${disabled ? "cursor-not-allowed" : "cursor-crosshair"}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          // Deliberately NO onPointerLeave. setPointerCapture already
          // guarantees pointerup reaches us even when the pointer is released
          // outside the canvas, so a leave handler is redundant, and browsers
          // differ on whether pointerleave fires mid-drag while capture is
          // held. Where it does fire, it would cut a signature short the
          // moment the mouse strayed past the edge of the box.
          aria-label="Signature drawing area. Draw your signature using your mouse, finger, or stylus."
          role="img"
        />
        {/* Signing guide line, hidden once there is ink so it is never
            mistaken for part of the signature. */}
        {!hasInk && (
          <div className="absolute inset-x-6 bottom-8 pointer-events-none">
            <div className={`h-px ${guide}`} />
            <div className={`text-[11px] mt-1.5 text-center ${label}`}>Draw your signature above the line</div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className={`text-[11px] ${label}`}>
          {hasInk ? "Signature captured." : "Mouse, finger, or stylus."}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={undo}
            disabled={disabled || !hasInk}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${btn}`}
          >
            Undo
          </button>
          <button
            type="button"
            onClick={clear}
            disabled={disabled || !hasInk}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${btn}`}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
